/* eslint-disable max-lines */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { fetchFoldersThunk } from './folder';
import FileApi from '../../apis/file';
import { isErrorWithMessage } from '../../helper';
import { resettingFileLoader } from '../slices/file';
import { RootState } from '../../redux/store';
import { AssignFileParams, CreateFileParams, CreateWordDocParams, FetchFolderFileParams } from '../../params/redux/file';

export const fetchFilesThunk = createAsyncThunk(
    'file/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.fetchFiles(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const fetchAssignFilesThunk = createAsyncThunk(
    'file/fetch-assigned',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.fetchAssignFiles(state.app.token);
            if (response?.success === 1) {
                return response?.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const fetchFolderFilesThunk = createAsyncThunk(
    'file/fetch-folder-files',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data: FetchFolderFileParams = {
                folder_id: state.folder.selectedFolder?.id as number,
                client_id
            };
            const response = await FileApi.fetchFolderFiles(state.app.token, data);
            if (response?.success === 1) {
                return response?.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const createFileThunk = createAsyncThunk(
    'file/create',
    async (data: CreateFileParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.createFile(state.app.token, data);
            if (response?.success === 1) {
                if (state.folder.selectedFolder) {
                    await dispatch(fetchFolderFilesThunk(data.client_id)).unwrap();
                } else {
                    await dispatch(fetchFoldersThunk(data.client_id)).unwrap();
                    await dispatch(fetchFilesThunk(data.client_id)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const dragFileToFolderThunk = createAsyncThunk(
    'file/drag',
    async ({ fileId, folderId, client_id }: { fileId: number, folderId: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.dragFileToFolder(state.app.token, fileId, folderId);
            if (response?.success === 1) {
                if (state.folder.selectedFolder) {
                    await dispatch(fetchFolderFilesThunk(client_id)).unwrap();
                } else {
                    await dispatch(fetchFoldersThunk(client_id)).unwrap();
                    await dispatch(fetchFilesThunk(client_id)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);


export const createDocFileThunk = createAsyncThunk(
    'file/create-doc',
    async (data: CreateWordDocParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.createDocumentFile(state.app.token, data);
            if (response?.success === 1) {
                if (state.folder.selectedFolder) {
                    await dispatch(fetchFolderFilesThunk(data.client_id)).unwrap();
                } else {
                    await dispatch(fetchFoldersThunk(data.client_id)).unwrap();
                    await dispatch(fetchFilesThunk(data.client_id)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const deleteFileThunk = createAsyncThunk(
    'file/delete',
    async ({ file_id, client_id }: { file_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.deleteFile(state.app.token, client_id, file_id);
            if (response?.success === 1) {
                if (state.folder.selectedFolder) {
                    await dispatch(fetchFolderFilesThunk(client_id)).unwrap();
                } else {
                    await dispatch(fetchFilesThunk(client_id)).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const fetchRecentFilesThunk = createAsyncThunk(
    'file/recent',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.fetchRecentFiles(state.app.token, client_id);
            if (response?.success === 1) {
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const getWordDocumentThunk = createAsyncThunk(
    'file/word-doc',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.getWordDocument(state.app.token, state.file.selectedFile?.id || 0);
            if (response?.success === 1) {
                return response.html;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const fetchSingleFileDetailsThunk = createAsyncThunk(
    'file/fetch-detail',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.fetchSingleFileDetails(state.app.token, state.file.selectedFile?.id || 0);
            if (response?.success === 1) {
                return response.file_info;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const assignTaskToFileThunk = createAsyncThunk(
    'file/assign-task',
    async ({ task_id, client_id }: { task_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.assignTaskToFile(state.app.token, task_id, client_id, state.file.selectedFile?.id || 0);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const assignFileThunk = createAsyncThunk(
    'file/assign',
    async (data: AssignFileParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.assignFile(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response?.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const removeAssingFileThunk = createAsyncThunk(
    'file/remove-assignee',
    async (data: AssignFileParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.removeAssingFile(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response?.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const addFileCommentThunk = createAsyncThunk(
    'file/add-comment',
    async (comment: string, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.addFileComment(state.app.token, state.file.selectedFile?.id || 0, comment);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const deleteFileTaskThunk = createAsyncThunk(
    'file/delete-task',
    async ({ task_id, client_id }: { task_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.deleteFileTask(state.app.token, task_id, state.file.selectedFile?.id || 0, client_id);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const deleteFileCommentThunk = createAsyncThunk(
    'file/delete-comment',
    async (comment_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.deleteFileComment(state.app.token, comment_id);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const editFileNameThunk = createAsyncThunk(
    'file/edit-name',
    async ({ file_name, client_id }: { file_name: string, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.editFileName(state.app.token, state.file.selectedFile?.id || 0, file_name, client_id);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);

export const editWordDocumentThunk = createAsyncThunk(
    'file/edit-doc',
    async ({ html, file_name }: { html: string, file_name: string }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FileApi.editWordDocument(state.app.token, html, file_name, state.file.selectedFile?.id || 0);
            if (response?.success === 1) {
                await dispatch(fetchSingleFileDetailsThunk()).unwrap();
                await dispatch(getWordDocumentThunk()).unwrap();
                return response.files;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFileLoader());
        }
    }
);